import { Check, Minus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { describeQualityFlags } from './format'
import { MATCH_CONDITIONS, type MatchCondition, type MatchEvidenceView } from './match-tier'

export interface MatchConditionsProps {
  /** Formatted similarity — the figure behind the first condition. */
  score: string
  evidence: MatchEvidenceView
  /** Raw quality flag names of the face; translated here. */
  qualityFlags: readonly string[]
}

/**
 * The four conditions of a *suggested match*, each with the figure it was
 * judged on, ticked where the API says it holds. Pure: the verdict comes from
 * the API, so the thresholds never reach the UI — the analyst sees why a
 * strong-looking suggestion is only listed, not a rule copied here.
 */
export function MatchConditions({ score, evidence, qualityFlags }: MatchConditionsProps) {
  const { t } = useTranslation()
  const base = 'cases.investigate.entityResolution.photoMatching.comparison.conditions'
  const unmet = new Set(evidence.unmetConditions)

  const figure: Record<MatchCondition, string> = {
    similarity: score,
    quality: describeQualityFlags(qualityFlags, t, t(`${base}.noWarnings`)),
    agreement:
      evidence.referenceCount < 2
        ? t(`${base}.singleReference`)
        : evidence.scoreSecond === null
          ? t(`${base}.noSecond`)
          : t(`${base}.second`, { score: evidence.scoreSecond }),
    margin:
      evidence.rivalScore === null
        ? t(`${base}.noRival`)
        : t(`${base}.rival`, { score: evidence.rivalScore }),
  }

  return (
    <ul className="flex flex-col gap-2">
      {MATCH_CONDITIONS.map(condition => {
        const met = !unmet.has(condition)
        const Icon = met ? Check : Minus
        return (
          <li
            key={condition}
            data-condition={condition}
            data-met={met ? 'true' : 'false'}
            className="flex items-start gap-2"
          >
            <Icon
              aria-hidden
              className={cn('mt-0.5 size-4 shrink-0', met ? 'text-success' : 'text-warning')}
            />
            <span className="flex min-w-0 flex-col">
              <span>
                {t(`${base}.${condition}`)}
                <span className="sr-only"> — {t(met ? `${base}.met` : `${base}.unmet`)}</span>
              </span>
              <span className="text-xs tabular-nums text-muted-foreground">
                {figure[condition]}
              </span>
            </span>
          </li>
        )
      })}
    </ul>
  )
}
